import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { format, isPast, isToday } from 'date-fns'
import { Bell, Plus, Trash2, Repeat, Clock, User, AlarmClockPlus } from 'lucide-react'
import { EmptyState } from '@/components/EmptyState'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  useReminders,
  useCreateReminder,
  useUpdateReminder,
  useDeleteReminder,
} from '@/hooks/use-reminders'
import type { Reminder } from '@/lib/types'

type FilterType = 'all' | 'birthday' | 'follow_up' | 'custom'

const recurringOptions = [
  { value: 'none', label: 'One-time' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
]

export default function RemindersPage() {
  const { t } = useTranslation()
  const [filter, setFilter] = useState<FilterType>('all')
  const [page, setPage] = useState(1)
  const [showForm, setShowForm] = useState(false)

  const typeParam = filter === 'all' ? undefined : filter
  const { data, isLoading } = useReminders(page, typeParam)
  const createReminder = useCreateReminder()
  const updateReminder = useUpdateReminder()
  const deleteReminder = useDeleteReminder()

  const [snoozeMenuId, setSnoozeMenuId] = useState<string | null>(null)

  const [formTitle, setFormTitle] = useState('')
  const [formType, setFormType] = useState('custom')
  const [formRemindAt, setFormRemindAt] = useState('')
  const [formRecurring, setFormRecurring] = useState('none')
  const [formInterval, setFormInterval] = useState(1)
  const [formEndDate, setFormEndDate] = useState('')

  function handleCreate(e: FormEvent) {
    e.preventDefault()
    if (!formTitle.trim() || !formRemindAt) return
    createReminder.mutate(
      {
        title: formTitle.trim(),
        type: formType,
        remind_at: new Date(formRemindAt).toISOString(),
        recurring: formRecurring,
        recurrence_interval: formRecurring !== 'none' ? formInterval : undefined,
        recurrence_end_date:
          formRecurring !== 'none' && formEndDate ? new Date(formEndDate).toISOString() : undefined,
      },
      {
        onSuccess: () => {
          setFormTitle('')
          setFormRemindAt('')
          setFormRecurring('none')
          setFormInterval(1)
          setFormEndDate('')
          setShowForm(false)
        },
      },
    )
  }

  function handleMarkSent(reminder: Reminder) {
    updateReminder.mutate({ id: reminder.id, sent: true })
  }

  function handleSnooze(reminder: Reminder, days: number) {
    const newDate = new Date()
    newDate.setDate(newDate.getDate() + days)
    newDate.setHours(9, 0, 0, 0)
    updateReminder.mutate({ id: reminder.id, remind_at: newDate.toISOString(), sent: false })
    setSnoozeMenuId(null)
  }

  const filters: { key: FilterType; label: string }[] = [
    { key: 'all', label: t('reminders.upcoming', 'All') },
    { key: 'birthday', label: t('reminders.birthdays', 'Birthdays') },
    { key: 'follow_up', label: t('reminders.followUps', 'Follow-ups') },
    { key: 'custom', label: t('reminders.custom', 'Custom') },
  ]

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const totalPages = Math.ceil(total / 20)

  return (
    <div className="flex flex-1 flex-col px-6 pt-6 pb-8">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold">{t('reminders.title')}</h1>
        <Button size="sm" onClick={() => setShowForm(!showForm)}>
          <Plus className="mr-1 size-4" />
          Add
        </Button>
      </div>

      {/* Filter tabs */}
      <div className="bg-surface-container mb-4 flex gap-1 overflow-x-auto rounded-lg p-1">
        {filters.map((f) => (
          <button
            key={f.key}
            onClick={() => {
              setFilter(f.key)
              setPage(1)
            }}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm font-medium whitespace-nowrap transition-colors',
              filter === f.key
                ? 'bg-surface-container-highest text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Create form */}
      {showForm && (
        <form
          onSubmit={handleCreate}
          className="bg-surface-container-low mb-4 space-y-3 rounded-xl p-4"
        >
          <input
            type="text"
            placeholder="Reminder title..."
            value={formTitle}
            onChange={(e) => setFormTitle(e.target.value)}
            className="border-outline-variant/30 bg-surface-container placeholder:text-muted-foreground focus:border-ring focus:ring-ring w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-1"
          />
          <div className="flex gap-2">
            <select
              value={formType}
              onChange={(e) => setFormType(e.target.value)}
              className="border-outline-variant/30 bg-surface-container rounded-lg border px-3 py-2 text-sm"
            >
              <option value="custom">Custom</option>
              <option value="birthday">Birthday</option>
              <option value="follow_up">Follow-up</option>
            </select>
            <input
              type="datetime-local"
              value={formRemindAt}
              onChange={(e) => setFormRemindAt(e.target.value)}
              className="border-outline-variant/30 bg-surface-container flex-1 rounded-lg border px-3 py-2 text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <Repeat className="text-muted-foreground size-4" />
            <select
              value={formRecurring}
              onChange={(e) => setFormRecurring(e.target.value)}
              className="border-outline-variant/30 bg-surface-container rounded-lg border px-3 py-2 text-sm"
            >
              {recurringOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          {formRecurring !== 'none' && (
            <div className="flex items-center gap-2">
              <label className="text-muted-foreground text-xs whitespace-nowrap">Every</label>
              <input
                type="number"
                min={1}
                value={formInterval}
                onChange={(e) => setFormInterval(Math.max(1, Number(e.target.value)))}
                className="border-outline-variant/30 bg-surface-container w-16 rounded-lg border px-2 py-2 text-sm"
              />
              <span className="text-muted-foreground text-xs">
                {formRecurring === 'daily'
                  ? 'day(s)'
                  : formRecurring === 'weekly'
                    ? 'week(s)'
                    : formRecurring === 'monthly'
                      ? 'month(s)'
                      : 'year(s)'}
              </span>
              <label className="text-muted-foreground ml-2 text-xs whitespace-nowrap">Until</label>
              <input
                type="date"
                value={formEndDate}
                onChange={(e) => setFormEndDate(e.target.value)}
                className="border-outline-variant/30 bg-surface-container flex-1 rounded-lg border px-2 py-2 text-sm"
                placeholder="No end date"
              />
            </div>
          )}
          <div className="flex gap-2">
            <Button
              type="submit"
              size="sm"
              disabled={!formTitle.trim() || !formRemindAt || createReminder.isPending}
            >
              {createReminder.isPending ? 'Creating...' : 'Create'}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
          </div>
        </form>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="bg-surface-container-high h-16 animate-pulse rounded-lg" />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && items.length === 0 && (
        <EmptyState
          icon={Bell}
          title={t('reminders.empty')}
          description={t('reminders.emptyHint', 'Create a reminder to stay on top of things.')}
          ctaLabel={t('reminders.addReminder', 'Add Reminder')}
          onCtaClick={() => setShowForm(true)}
        />
      )}

      {/* Reminder list */}
      {!isLoading && items.length > 0 && (
        <div className="space-y-2">
          {items.map((reminder) => {
            const remindDate = new Date(reminder.remindAt)
            const overdue = isPast(remindDate) && !isToday(remindDate)
            return (
              <div
                key={reminder.id}
                className={cn(
                  'bg-surface-container-low flex items-start gap-3 rounded-xl p-3',
                  reminder.sent && 'opacity-50',
                  overdue && !reminder.sent && 'border-error/30 border',
                )}
              >
                <div
                  className={cn(
                    'flex size-8 shrink-0 items-center justify-center rounded-full',
                    reminder.type === 'birthday'
                      ? 'bg-coral-soft text-coral'
                      : 'bg-lilac-soft text-lilac-foreground dark:text-lilac',
                  )}
                >
                  {reminder.type === 'birthday' ? (
                    <User className="size-4" />
                  ) : (
                    <Bell className="size-4" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className={cn('text-sm font-medium', reminder.sent && 'line-through')}>
                    {reminder.title}
                  </p>
                  <div className="text-muted-foreground flex flex-wrap items-center gap-2 text-xs">
                    <span className="flex items-center gap-1">
                      <Clock className="size-3" />
                      {format(remindDate, 'MMM d, yyyy h:mm a')}
                    </span>
                    {reminder.recurring !== 'none' && (
                      <span className="flex items-center gap-1">
                        <Repeat className="size-3" />
                        {reminder.recurrenceInterval > 1
                          ? `Every ${reminder.recurrenceInterval} ${reminder.recurring === 'daily' ? 'days' : reminder.recurring === 'weekly' ? 'weeks' : reminder.recurring === 'monthly' ? 'months' : 'years'}`
                          : reminder.recurring}
                      </span>
                    )}
                    {overdue && !reminder.sent && (
                      <span className="text-coral font-medium">Overdue</span>
                    )}
                  </div>
                </div>
                <div className="relative flex shrink-0 items-center gap-1">
                  {!reminder.sent && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => handleMarkSent(reminder)}
                        title="Mark as done"
                      >
                        <Bell className="size-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() =>
                          setSnoozeMenuId(snoozeMenuId === reminder.id ? null : reminder.id)
                        }
                        title="Snooze"
                      >
                        <AlarmClockPlus className="size-3.5" />
                      </Button>
                    </>
                  )}
                  <button
                    onClick={() => deleteReminder.mutate(reminder.id)}
                    className="text-muted-foreground hover:text-destructive shrink-0 transition-colors"
                  >
                    <Trash2 className="size-3.5" />
                  </button>

                  {snoozeMenuId === reminder.id && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setSnoozeMenuId(null)} />
                      <div className="border-outline-variant/20 bg-surface-container-highest/90 absolute top-full right-0 z-50 mt-1 w-36 rounded-xl py-1 shadow-lg backdrop-blur-xl">
                        <button
                          className="hover:bg-primary/5 flex w-full items-center px-3 py-1.5 text-sm"
                          onClick={() => handleSnooze(reminder, 1)}
                        >
                          1 day
                        </button>
                        <button
                          className="hover:bg-primary/5 flex w-full items-center px-3 py-1.5 text-sm"
                          onClick={() => handleSnooze(reminder, 7)}
                        >
                          1 week
                        </button>
                        <button
                          className="hover:bg-primary/5 flex w-full items-center px-3 py-1.5 text-sm"
                          onClick={() => handleSnooze(reminder, 30)}
                        >
                          1 month
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </Button>
          <span className="text-muted-foreground text-xs">
            {page} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  )
}
